import { Button, Checkbox, VStack, Text, Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { useState } from 'react'
import type { ScopeType, BoundingBox } from '../../types/analyticsMap'
import type { FilterGroupRequest } from '../../types/contactList'
import type { TagOption } from '../../types/contactList'
import { useApplyTags } from '../../hooks/useAnalyticsMap'
import { extractApiMessage } from '../../lib/apiError'
import { toaster } from '@/components/ui/toaster'

interface ApplyTagsModalProps {
  isOpen: boolean
  onClose: () => void
  tags: TagOption[]
  scope: ScopeType
  filterGroups: FilterGroupRequest[]
  profileNames?: string[]
  polygonWkt?: string
  bBox?: BoundingBox
  thresholds?: Record<string, number>
}

export function ApplyTagsModal({
  isOpen,
  onClose,
  tags,
  scope,
  filterGroups,
  profileNames,
  polygonWkt,
  bBox,
  thresholds,
}: ApplyTagsModalProps) {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set())
  const applyMutation = useApplyTags()

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds(prev => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }
      return next
    })
  }

  const handleApply = () => {
    if (selectedTagIds.size === 0) return
    applyMutation.mutate(
      {
        filterGroups,
        profileNames,
        polygonWkt,
        bBox,
        scope,
        tagIds: Array.from(selectedTagIds),
        thresholds,
      },
      {
        onSuccess: (data) => {
          toaster.create({
            title: 'Tags applied',
            description: `Applied tags to ${data.appliedCount} contacts.`,
            type: 'success',
            duration: 4000,
            closable: true,
          })
          setSelectedTagIds(new Set())
          onClose()
        },
        onError: (error) => {
          toaster.create({
            title: 'Failed to apply tags',
            description: extractApiMessage(error, 'Could not apply tags. Please try again.'),
            type: 'error',
            duration: 4000,
            closable: true,
          })
        },
      }
    )
  }

  const handleClose = () => {
    setSelectedTagIds(new Set())
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} size='md' onOpenChange={e => {
      if (!e.open) {
        handleClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="md">Apply Tags</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Select tags to apply to the matching contacts.
              </Text>
              <VStack align="start" gap={2} maxH="300px" overflowY="auto">
                {tags.map(tag => (
                  <Checkbox.Root
                    key={tag.id}
                    size="sm"
                    onCheckedChange={() => handleToggleTag(tag.id)}
                    checked={selectedTagIds.has(tag.id)}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                    <Text fontSize="sm">{tag.label}</Text>
                  </Checkbox.Label></Checkbox.Root>
                ))}
                {tags.length === 0 && (
                  <Text fontSize="sm" color="gray.400">No tags available</Text>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button size="sm" variant="ghost" mr={2} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorPalette="brand"
                onClick={handleApply}
                loading={applyMutation.isPending}
                disabled={selectedTagIds.size === 0}
              >
                Apply Tags
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
